import Decimal from 'decimal.js-light';

import { ClientInputError } from '@/lib/errors/client';

import { CHAIN_IDS_SUPPORT_EIP1559, CHAIN_IDS_SUPPORT_GASLESS } from '@/constants/chain';

import ENV from '@/environment/server';

import { GasFeePreset } from '@/store/tradeSettings/market';

import { TokenResult } from './0x/token-registry.types';
import { isWrap } from './swap';

interface Eip1559Fees {
  maxPriorityFeePerGas: bigint;
  maxFeePerGas: bigint;
  baseFeePerGas: bigint;
}
interface Eip1559FeesRaw {
  maxPriorityFeePerGas: string;
  maxFeePerGas: string;
  baseFeePerGas: string;
}

interface GasInfoTypeLegacy {
  gasPrice: bigint;
  suggested: bigint;
  custom: bigint;
  type: 'legacy';
}

interface GasInfoTypeLegacyRaw {
  suggested: string;
  custom: string;
  type: 'legacy';
}

interface GasInfoTypeEIP1559 {
  gasPrice: bigint;
  suggested: Eip1559Fees;
  custom: Eip1559Fees;
  type: 'eip1559';
}

interface GasInfoTypeEIP1559Raw {
  suggested: Eip1559FeesRaw;
  custom: Eip1559FeesRaw;
  type: 'eip1559';
}

interface GasResponseData {
  legacy: LegacyData;
  eip1559: Eip1559Data | null;
}

interface LegacyData {
  timestampSeconds: string;
  instant: string;
  fast: string;
  standard: string;
  slow: string;
}

interface Eip1559Data {
  timestampSeconds: string;
  baseFeePerGas: string;
  instant: Eip1559FeeData;
  fast: Eip1559FeeData;
  standard: Eip1559FeeData;
  slow: Eip1559FeeData;
}

interface Eip1559FeeData {
  maxFeePerGas: string;
  maxPriorityFeePerGas: string;
}

export type GasInfo = GasInfoTypeLegacy | GasInfoTypeEIP1559;

export type GasInfoRaw = GasInfoTypeLegacyRaw | GasInfoTypeEIP1559Raw;

export const getGasPrices = async (chainId: number): Promise<GasInfoRaw> => {
  if (!ENV.ZEROEX_API_KEY) throw new Error('ZEROEX_API_KEY is not set');

  const response = await fetch(`https://${ENV.ZEROEX_API_BASE_URL}/gas/all?chainId=${chainId}`, {
    headers: {
      '0x-api-key': ENV.ZEROEX_API_KEY,
      '0x-version': 'v1',
    },
  });

  if (!response.ok) throw new Error(response.statusText);

  const data: GasResponseData = await response.json();

  if (data.eip1559 !== null) {
    return {
      suggested: {
        maxPriorityFeePerGas: data.eip1559.fast.maxPriorityFeePerGas,
        maxFeePerGas: data.eip1559.fast.maxFeePerGas,
        baseFeePerGas: data.eip1559.baseFeePerGas,
      },
      custom: {
        maxPriorityFeePerGas: data.eip1559.fast.maxPriorityFeePerGas,
        maxFeePerGas: data.eip1559.fast.maxFeePerGas,
        baseFeePerGas: data.eip1559.baseFeePerGas,
      },
      type: 'eip1559',
    };
  } else {
    return {
      suggested: data.legacy.fast,
      custom: data.legacy.fast,
      type: 'legacy',
    };
  }
};

interface GasOptionArgs {
  fees: GasInfo;
  gasFeePreset: GasFeePreset;
  chainId: number;
  customGasPrice?: string | undefined;
  customPriorityFee?: string | undefined;
  customBaseFee?: string | undefined;
}

export type GasOptionsResponse =
  | {
      type: 'eip1559';
      maxFeePerGas: bigint | undefined;
      maxPriorityFeePerGas: bigint | undefined;
    }
  | {
      gasPrice: bigint | undefined;
      type: 'legacy';
    };

export const getGasOptionsFromSettings = ({
  fees,
  gasFeePreset,
  chainId,
  customGasPrice,
  customPriorityFee,
  customBaseFee,
}: GasOptionArgs): GasOptionsResponse => {
  if (!fees) throw new ClientInputError('getGasOptionsFromSettings: fees must not be undefined');
  const selectedGasData = fees[gasFeePreset];

  // if a user sets a base fee, we need to ensure that it is not lower than
  // the current base fee.
  let baseFee = (selectedGasData as Eip1559Fees)?.baseFeePerGas;

  if (gasFeePreset === 'custom' && customBaseFee) {
    const userBaseFee = BigInt(customBaseFee);
    if (userBaseFee >= baseFee) {
      baseFee = userBaseFee;
    }
  }

  // handle eip1559 gas
  if (CHAIN_IDS_SUPPORT_EIP1559.includes(chainId)) {
    if (fees.type === 'eip1559') {
      const priorityFeePerGas =
        gasFeePreset === 'custom' && customPriorityFee
          ? BigInt(customPriorityFee)
          : (selectedGasData as Eip1559Fees)?.maxPriorityFeePerGas;

      const maxFeePerGas =
        gasFeePreset === 'custom' ? baseFee + priorityFeePerGas : fees.suggested.maxFeePerGas;

      return {
        maxFeePerGas,
        maxPriorityFeePerGas: priorityFeePerGas,
        type: 'eip1559',
      };
    } else {
      throw new Error(`gas oracle response ${fees.type} doesn't match chain ID ${chainId}`);
    }
  }

  // handle legacy gas
  if (fees.type === 'legacy') {
    const gasPrice =
      gasFeePreset === 'custom' && customGasPrice
        ? BigInt(customGasPrice)
        : (selectedGasData as bigint);

    return {
      gasPrice,
      type: 'legacy',
    };
  } else {
    throw new Error(`gas oracle response of type ${fees.type} doesn't match chain ID ${chainId}`);
  }
};

/**
 * Convert a wei value to gwei, rouding up.
 * @param wei - Wei value
 * @returns Gwei value
 */
export const roundUpWeiToGwei = (wei: bigint | string) => {
  const input = typeof wei === 'bigint' ? wei.toString() : wei;
  return new Decimal(input).div(1e9).toDecimalPlaces(0).toString();
};

/**
 * Check if the trade supports gasless by referencing the sell token info.
 * @param buyToken - TokenResult (optional) - Used to check the 'type'.
 * @param sellToken - TokenResult (optional) - Used to check the 'type'.
 * @returns boolean - Is gasless supported for this chain and/or sell token.
 */
export function getIsGaslessSupported(
  buyToken: TokenResult | undefined,
  sellToken: TokenResult | undefined,
): boolean {
  const assetIsNotNative = sellToken?.type !== 'Native';
  const chainSupportsGasless = !!sellToken && CHAIN_IDS_SUPPORT_GASLESS.includes(sellToken.chainId);
  const isWrapOrUnwrap =
    buyToken && sellToken ? isWrap(sellToken.address, buyToken.address, sellToken.chainId) : false;

  return !isWrapOrUnwrap && assetIsNotNative && chainSupportsGasless;
}
